import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronUpDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

export type AsyncTypeaheadOption = {
  id: string;
  label: string;
  description?: string;
};

type AsyncTypeaheadProps = {
  value: AsyncTypeaheadOption | null;
  onChange: (value: AsyncTypeaheadOption | null) => void;
  loadOptions: (query: string) => Promise<AsyncTypeaheadOption[]>;
  onCreateOption?: (query: string) => Promise<AsyncTypeaheadOption | null>;
  createOptionLabel?: (query: string) => string;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
};

export default function AsyncTypeahead({
  value,
  onChange,
  loadOptions,
  onCreateOption,
  createOptionLabel,
  placeholder = 'Search...',
  emptyMessage = 'No results found.',
  className = '',
  disabled = false,
}: AsyncTypeaheadProps) {
  const [query, setQuery] = useState(value?.label || '');
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [options, setOptions] = useState<AsyncTypeaheadOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const requestRef = useRef(0);
  const listboxId = useId();

  useEffect(() => {
    setQuery(value?.label || '');
  }, [value?.id, value?.label]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!rootRef.current || !target || rootRef.current.contains(target)) {
        return;
      }

      setOpen(false);
      setHasInteracted(false);
      if (value) {
        setQuery(value.label);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value]);

  useEffect(() => {
    if (!open || disabled) {
      return;
    }

    const timer = window.setTimeout(async () => {
      const req = requestRef.current + 1;
      requestRef.current = req;
      setLoading(true);

      try {
        const nextOptions = await loadOptions(query.trim());
        if (requestRef.current !== req) {
          return;
        }

        setOptions(nextOptions);
        setHighlightedIndex(0);
      } catch {
        if (requestRef.current === req) {
          setOptions([]);
        }
      } finally {
        if (requestRef.current === req) {
          setLoading(false);
        }
      }
    }, 220);

    return () => {
      window.clearTimeout(timer);
    };
  }, [open, query, loadOptions, disabled]);

  const showEmptyState = useMemo(() => !loading && hasInteracted && options.length === 0, [loading, hasInteracted, options.length]);
  const trimmedQuery = query.trim();
  const canCreateOption = Boolean(
    onCreateOption
    && trimmedQuery
    && !loading
    && hasInteracted
    && !options.some((option) => option.label.trim().toLowerCase() === trimmedQuery.toLowerCase()),
  );

  const selectOption = (option: AsyncTypeaheadOption) => {
    onChange(option);
    setQuery(option.label);
    setOpen(false);
    setHasInteracted(false);
  };

  const createOption = async () => {
    if (!onCreateOption || !trimmedQuery || creating) {
      return;
    }

    setCreating(true);
    try {
      const option = await onCreateOption(trimmedQuery);
      if (option) {
        selectOption(option);
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div ref={rootRef} className={`relative ${className}`.trim()}>
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
        <input
          type="text"
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          className="input-field w-full pl-11 pr-16"
          role="combobox"
          aria-controls={listboxId}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          onFocus={() => {
            if (disabled) return;
            setOpen(true);
            setHasInteracted(true);
          }}
          onChange={(event) => {
            const nextValue = event.target.value;
            setQuery(nextValue);
            setHasInteracted(true);
            setOpen(true);
            if (!nextValue.trim() && value) {
              onChange(null);
            }
          }}
          onKeyDown={(event) => {
            if (!open || options.length === 0) {
              if (event.key === 'ArrowDown') {
                setOpen(true);
              }
              if (event.key === 'Enter' && canCreateOption) {
                event.preventDefault();
                void createOption();
              }
              return;
            }

            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setHighlightedIndex((prev) => (prev + 1) % options.length);
              return;
            }

            if (event.key === 'ArrowUp') {
              event.preventDefault();
              setHighlightedIndex((prev) => (prev - 1 + options.length) % options.length);
              return;
            }

            if (event.key === 'Enter') {
              event.preventDefault();
              const picked = options[highlightedIndex];
              if (picked) {
                selectOption(picked);
              } else if (canCreateOption) {
                void createOption();
              }
              return;
            }

            if (event.key === 'Escape') {
              event.preventDefault();
              setOpen(false);
              setHasInteracted(false);
            }
          }}
        />

        {query ? (
          <button
            type="button"
            aria-label="Clear selection"
            className="absolute right-10 top-1/2 -translate-y-1/2 rounded p-1 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface"
            onClick={() => {
              setQuery('');
              setOptions([]);
              setHasInteracted(false);
              onChange(null);
              setOpen(true);
            }}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        ) : null}

        <ChevronUpDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
      </div>

      {open ? (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-40 mt-2 max-h-72 w-full overflow-y-auto rounded-xl border border-outline/60 bg-surface-container-lowest p-1.5 shadow-elevated"
        >
          {loading ? (
            <div className="px-3 py-2 text-sm text-on-surface-variant">Searching...</div>
          ) : null}

          {!loading && options.length > 0 ? (
            <ul className="space-y-1">
              {options.map((option, index) => {
                const isActive = index === highlightedIndex;
                return (
                  <li key={option.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      className={`w-full rounded-lg px-3 py-2 text-left transition-colors ${isActive ? 'bg-surface-container-high text-on-surface' : 'hover:bg-surface-container-low text-on-surface'}`}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => selectOption(option)}
                    >
                      <div className="text-sm font-medium">{option.label}</div>
                      {option.description ? (
                        <div className="mt-0.5 text-xs text-on-surface-variant">{option.description}</div>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {showEmptyState ? (
            <div className="px-3 py-2 text-sm text-on-surface-variant">{emptyMessage}</div>
          ) : null}

          {canCreateOption ? (
            <button
              type="button"
              className="mt-1 w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-primary transition-colors hover:bg-surface-container-low disabled:opacity-60"
              disabled={creating}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => { void createOption(); }}
            >
              {creating
                ? 'Creating...'
                : (createOptionLabel ? createOptionLabel(trimmedQuery) : `Create "${trimmedQuery}"`)}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
